import { Component, OnInit, Input, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { DashboardService } from "../../dashboard.service";
import { OpensAndViews } from "../../dashboard.model";
import { ChartDataset, Chart, ChartConfiguration } from "chart.js";
import  moment from "moment";

@Component({
    selector: "chart-opens-views",
    templateUrl: "./opens-views.component.html",
    styleUrls: ["./opens-views.component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OpensViewsComponent implements OnInit {
  @Input("opensAndViewsError")
  set opensAndViewsError(value: string) {
    this.error = value;
    this.isLoading = false;
  }
  get opensAndViewsError(): string {
    return this.error;
  }

  @Input("opensAndViewsData")
  set opensAndViewsData(value: OpensAndViews[]) {
    if(value === undefined) { 
      /* This keeps the spinner displayed while the chart data is being processed */ 
      this.isProcessing = true;
      return; 
    }

    this.data = value;

    if (this.data.length == 0) {
      this.showNoDataMessage = true;
      this.isProcessing = false;
    } else {
      this.showNoDataMessage = false;
      this.chartData();
    }
  }
  get opensAndViewsData(): OpensAndViews[] {
    return this.data;
  }

  private _days: number = 90;
  @Input("days")
  set days(value: number) {
    this._days = value;
  }
  get days(): number {
    return this._days;
  }

  @Input("isLoading") isLoading: boolean = false;

  isProcessing: boolean = false;
  data: OpensAndViews[];
  error: string;

  showNoDataMessage: boolean = false;

  public lineChartData: ChartDataset[] = [{ data: [], label: "" }];
  public lineChartLabels: string[] = [];
  public lineChartLegend = false;
  public lineChartType = "line";
  public lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, },        
    },
  };

  constructor(private api: DashboardService) {}

  ngOnInit() {
    if (!this.data) {
      this.isLoading = true;
    }
  }

  async chartData() {    
    if (!this.data) {
      return;
    }

    this.isProcessing = true;
    let lineThickness = 1;
    let pointSize = 2;

    var salesmails = {
      data: [],
      label: "SalesMails",
      backgroundColor: "rgba(0,112,66, .4)",
      borderColor: "#16B65A",
      pointBackgroundColor: "#16B65A",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var views = {
      data: [],
      label: "Views",
      backgroundColor: "rgba(126,189,66, .4)",
      borderColor: "#4B667B",
      pointBackgroundColor: "#4B667B",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var ctas = {
      data: [],
      label: "CTA Clicks",
      backgroundColor: "rgba(96,110, 255, .4)",
      borderColor: "#606eff",
      pointBackgroundColor: "#606eff",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var reactions = {
      data: [],
      label: "Reactions",
      backgroundColor: "rgba(255, 108, 71, .4)",
      borderColor: "#ff6c47",
      pointBackgroundColor: "#ff6c47",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var comments = {
      data: [],
      label: "Comments",
      backgroundColor: "rgba(255, 164, 71, .4)",
      borderColor: "#ffa447",
      pointBackgroundColor: "#ffa447",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var shares = {
      data: [],
      label: "Shares",
      backgroundColor: "rgba(169, 3, 252, .4)",
      borderColor: "#a903fc",
      pointBackgroundColor: "#a903fc",
      borderWidth: lineThickness,
      pointRadius: pointSize,
      pointHoverRadius: pointSize,
      fill: true,
      tension: 0.5
    };

    var o = this.data.filter((item) => item.type == "Open");
    var v = this.data.filter((item) => item.type == "View");
    var c = this.data.filter((item) => item.type == "ctas");
    var r = this.data.filter((item) => item.type == "reactions");
    var com = this.data.filter((item) => item.type == "comments");
    var sms = this.data.filter((item) => item.type == "salesmails");
    var s = this.data.filter((item) => item.type == "Share");

    var tempLabels = [];

    this.lineChartLabels = [];
    this.lineChartData = [];
    
    for (let i = this.days; i >= 0; i--) {
      var d = moment().subtract(i, "days");
      var dateString = d.format("M/D");

      var shareMatchingDays = s.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      var viewMatchingDays = v.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      var ctasMatchingDays = c.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      var reactionsMatchingDays = r.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      var commentsMatchingDays = com.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      var smsMatchingDays = sms.filter((item) => {
        var m = moment(item.cre_date);
        return m.format("M/D") == dateString;
      });

      tempLabels.push(dateString);
      views.data.push(viewMatchingDays.length);
      ctas.data.push(ctasMatchingDays.length);
      reactions.data.push(reactionsMatchingDays.length);
      comments.data.push(commentsMatchingDays.length);
      salesmails.data.push(smsMatchingDays.length);
      shares.data.push(shareMatchingDays.length);
    }

    this.lineChartLabels.push(...tempLabels);

    this.lineChartData.push(salesmails);    
    this.lineChartData.push(views);
    this.lineChartData.push(ctas);
    this.lineChartData.push(comments);
    this.lineChartData.push(reactions);
    this.lineChartData.push(shares);

    this.lineChartLegend = true; 
    this.isProcessing = false;
  }

  // events
  public chartClicked({
    event,
    active,
  }: {
    event: MouseEvent;
    active: {}[];
  }): void {
    //console.log(event, active);
  }

  public chartHovered({
    event,
    active,
  }: {
    event: MouseEvent;
    active: {}[];
  }): void {
    //console.log(event, active);
  }
}
